import { useState, useRef } from "react";
import { BlockRenderer } from "@donex/preview";
import type { Block } from "@donex/preview";

type Phase = "idle" | "approved" | "denied";

const INITIAL_BLOCKS: Block[] = [
  {
    type: "header",
    text: { type: "plain_text", text: "💰  Expense Approval Request", emoji: true },
  },
  { type: "divider" },
  {
    type: "section",
    fields: [
      { type: "mrkdwn", text: "*Submitted by*\nSarah Chen" },
      { type: "mrkdwn", text: "*Amount*\n$1,250.00" },
      { type: "mrkdwn", text: "*Category*\nTravel & Lodging" },
      { type: "mrkdwn", text: "*Date*\nJun 25, 2026" },
    ],
  },
  {
    type: "section",
    text: {
      type: "mrkdwn",
      text: "Team offsite accommodation in Austin, TX — 2 nights at the Marriott Downtown.",
    },
  },
  {
    type: "input",
    label: { type: "plain_text", text: "Comment", emoji: false },
    optional: true,
    element: {
      type: "plain_text_input",
      action_id: "comment",
      placeholder: { type: "plain_text", text: "Add a note before approving or denying…" },
      multiline: false,
    },
  },
  { type: "divider" },
  {
    type: "actions",
    elements: [
      {
        type: "button",
        text: { type: "plain_text", text: "✓  Approve", emoji: true },
        action_id: "approve",
        value: "approve",
        style: "primary",
      },
      {
        type: "button",
        text: { type: "plain_text", text: "✕  Deny", emoji: true },
        action_id: "deny",
        value: "deny",
        style: "danger",
      },
    ],
  },
];

const APPROVED_BLOCKS: Block[] = [
  {
    type: "alert",
    text: { type: "mrkdwn", text: "*Approved* by Alex Rivera · Jun 27, 2026" },
    level: "success",
  },
  {
    type: "section",
    fields: [
      { type: "mrkdwn", text: "*Submitted by*\nSarah Chen" },
      { type: "mrkdwn", text: "*Amount*\n$1,250.00" },
      { type: "mrkdwn", text: "*Category*\nTravel & Lodging" },
      { type: "mrkdwn", text: "*Date*\nJun 25, 2026" },
    ],
  },
  {
    type: "context",
    elements: [{ type: "mrkdwn", text: "Approved at Jun 27, 2026 at 9:14 AM" }],
  },
  {
    type: "actions",
    elements: [
      {
        type: "button",
        text: { type: "plain_text", text: "↩  Undo approval", emoji: true },
        action_id: "undo",
        value: "undo",
      },
    ],
  },
];

const DENIED_BLOCKS: Block[] = [
  {
    type: "alert",
    text: { type: "mrkdwn", text: "*Denied* by Alex Rivera · Jun 27, 2026" },
    level: "error",
  },
  {
    type: "section",
    fields: [
      { type: "mrkdwn", text: "*Submitted by*\nSarah Chen" },
      { type: "mrkdwn", text: "*Amount*\n$1,250.00" },
      { type: "mrkdwn", text: "*Category*\nTravel & Lodging" },
      { type: "mrkdwn", text: "*Date*\nJun 25, 2026" },
    ],
  },
  {
    type: "context",
    elements: [{ type: "mrkdwn", text: "Denied at Jun 27, 2026 at 9:14 AM" }],
  },
  {
    type: "actions",
    elements: [
      {
        type: "button",
        text: { type: "plain_text", text: "↩  Undo denial", emoji: true },
        action_id: "undo",
        value: "undo",
      },
    ],
  },
];

type LogEntry = {
  id: string;
  direction: "out" | "in";
  detail: string;
  isNew: boolean;
};

const INITIAL_LOG: LogEntry[] = [
  {
    id: "initial-send",
    direction: "out",
    detail: "You sent the full flow in one request",
    isNew: false,
  },
];

const STEP_TEMPLATES = [
  {
    direction: "out" as const,
    method: "POST",
    path: "/messages",
    placeholder: "Full flow not yet sent",
  },
  {
    direction: "in" as const,
    method: "CALLBACK",
    path: "→ your endpoint",
    placeholder: "Waiting for an action…",
  },
  {
    direction: "out" as const,
    method: "UPDATE MESSAGE",
    path: "🪄 Automatically",
    placeholder: "",
  },
];

export function InteractiveDemo() {
  const [blocks, setBlocks] = useState<Block[]>(INITIAL_BLOCKS);
  const [fading, setFading] = useState(false);
  const [log, setLog] = useState<LogEntry[]>(INITIAL_LOG);
  const processing = useRef(false);

  const doReset = () => {
    setFading(true);
    setTimeout(() => {
      processing.current = false;
      setBlocks(INITIAL_BLOCKS);
      setLog(INITIAL_LOG);
      setFading(false);
    }, 220);
  };

  const handleAction = (action_id: string) => {
    if (action_id === "undo") {
      doReset();
      return;
    }
    if (processing.current) return;
    processing.current = true;

    const isApprove = action_id === "approve";

    setLog((prev) => [
      ...prev,
      {
        id: "callback",
        direction: "in",
        detail: "We forwarded important data to your endpoint",
        isNew: true,
      },
    ]);

    setTimeout(() => {
      setFading(true);
      setTimeout(() => {
        setBlocks(isApprove ? APPROVED_BLOCKS : DENIED_BLOCKS);
        setFading(false);
        setLog((prev) => [
          ...prev,
          {
            id: "replace",
            direction: "out",
            detail: "Done",
            isNew: true,
          },
        ]);
      }, 280);
    }, 550);
  };

  const steps = STEP_TEMPLATES.map((template, i) => ({
    ...template,
    detail: log[i]?.detail ?? null,
    isNew: log[i]?.isNew ?? false,
    active: i < log.length,
  }));

  const hint =
    log.length === 1
      ? "Click a button to watch the cycle."
      : log.length === 3
        ? "Full cycle complete."
        : "Replacing the message…";

  return (
    <section
      id="demo"
      className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-16 snap-start"
    >
      <div className="w-full max-w-4xl space-y-12">
        {/* Heading */}
        <div className="text-center space-y-3">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            One request to send your Custom Flow.
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "var(--gradient-primary)" }}
            >
              We keep and handle the rest.
            </span>
          </h2>
          <p className="mx-auto max-w-lg text-base leading-relaxed text-muted-foreground">
            Define your message, inputs, and buttons in a single API call. When someone acts in
            Slack, we forward only what matters — a clean payload with the action and values —
            straight to your callback. The message updates automatically.
          </p>
        </div>

        {/* Two-column: preview (left) + event flow (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6 lg:gap-16 items-start">
          {/* Slack preview */}
          <div style={{ position: "relative", borderRadius: 10 }}>
            {/* Animated green glow ring — desktop only */}
            <div
              className="hidden lg:block"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: 10,
                border: "1px solid rgba(0,166,124,0.8)",
                boxShadow:
                  "0 0 18px rgba(0,166,124,0.5), 0 0 40px rgba(0,166,124,0.25), inset 0 0 12px rgba(0,166,124,0.08)",
                pointerEvents: "none",
                zIndex: 10,
                animation: "preview-glow-breathe 2.8s ease-in-out infinite",
              }}
            />
            <div
              style={{
                borderRadius: 10,
                overflow: "hidden",
                boxShadow: "0 40px 100px -24px rgba(0,100,70,0.5), 0 8px 28px rgba(0,0,0,0.45)",
              }}
            >
              {/* Channel bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 16px",
                  background: "#222529",
                  borderBottom: "1px solid rgba(255,255,255,0.07)",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#9a9b9e" }}>#</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#d1d2d3" }}>
                  expense-approvals
                </span>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.18)" }}>·</span>
                <span style={{ fontSize: 13, color: "#9a9b9e" }}>Slack</span>
              </div>

              {/* Message body */}
              <div
                style={{
                  background: "#1a1d21",
                  padding: "14px 16px 16px",
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  {/* Bot avatar */}
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 6,
                      flexShrink: 0,
                      background: "linear-gradient(135deg, #007a5a, #00a67c)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      fontWeight: 800,
                      color: "#fff",
                    }}
                  >
                    D
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Bot name row */}
                    <div
                      style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}
                    >
                      <span
                        style={{ fontWeight: 700, color: "#d1d2d3", fontSize: 15, lineHeight: 1 }}
                      >
                        Done[x]
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#1d1c1d",
                          background: "#e0e0e0",
                          padding: "1px 5px",
                          borderRadius: 3,
                          lineHeight: "16px",
                        }}
                      >
                        APP
                      </span>
                      <span style={{ fontSize: 12, color: "#9a9b9e" }}>9:13 AM</span>
                    </div>

                    {/* Animated block content */}
                    <div
                      style={{
                        transition: "opacity 0.22s ease, transform 0.22s ease",
                        opacity: fading ? 0 : 1,
                        transform: fading ? "translateY(4px)" : "translateY(0)",
                      }}
                    >
                      <BlockRenderer
                        blocks={blocks}
                        theme="dark"
                        container={false}
                        onAction={handleAction}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Event flow — vertical step cards + CTA */}
          <div className="flex flex-col gap-6">
            <div>
              <p className="text-xs text-muted-foreground mb-3">{hint}</p>
              <div className="flex flex-col">
                {steps.map((step, i) => (
                  <div key={i}>
                    <StepCard step={step} />
                    {i < 2 && <StepArrowDown active={step.active && steps[i + 1].active} />}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => {
                  const input = document.getElementById("email-input") as HTMLInputElement | null;
                  if (input) {
                    input.scrollIntoView({ behavior: "smooth", block: "center" });
                    setTimeout(() => input.focus(), 400);
                  }
                }}
                className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                style={{
                  backgroundImage: "var(--gradient-primary)",
                  boxShadow: "var(--shadow-elegant)",
                }}
              >
                Start for Free
              </button>
              <p className="mt-2 text-xs text-muted-foreground">Minutes, not a project.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

type StepData = {
  direction: "out" | "in";
  method: string;
  path: string;
  placeholder: string;
  detail: string | null;
  isNew: boolean;
  active: boolean;
};

function StepCard({ step }: { step: StepData }) {
  const isIn = step.direction === "in";
  const activeColor = isIn ? "#5ba4cf" : "#2eb67d";
  const color = step.active ? activeColor : "rgba(255,255,255,0.18)";
  const borderColor = step.active
    ? isIn
      ? "rgba(91,164,207,0.2)"
      : "rgba(46,182,125,0.22)"
    : "rgba(255,255,255,0.06)";

  return (
    <div
      style={{
        background: "#0d0f12",
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        padding: "12px",
        fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, "Courier New", monospace',
        fontSize: 11,
        transition: "border-color 0.4s ease",
        animation: step.isNew ? "log-fade-in 0.3s ease forwards" : "none",
      }}
    >
      <div style={{ display: "flex", gap: 5, alignItems: "baseline", marginBottom: 3 }}>
        <span style={{ color, fontSize: 10, flexShrink: 0 }}>{isIn ? "←" : "→"}</span>
        <span style={{ color, fontWeight: 700, fontSize: 11 }}>{step.method}</span>
      </div>
      <div
        style={{
          color: step.active ? "#9a9b9e" : "rgba(255,255,255,0.14)",
          fontSize: 10,
          marginBottom: 4,
          transition: "color 0.3s ease",
        }}
      >
        {step.path}
      </div>
      <div
        style={{
          color: step.active ? "#616061" : "rgba(255,255,255,0.1)",
          fontSize: 10,
          lineHeight: 1.4,
          transition: "color 0.3s ease",
        }}
      >
        {step.active ? step.detail : step.placeholder}
      </div>
    </div>
  );
}

function StepArrowDown({ active }: { active: boolean }) {
  const color = active ? "#2eb67d" : "rgba(255,255,255,0.12)";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "3px 0",
        transition: "color 0.4s ease",
      }}
    >
      <div
        style={{ width: 1, height: 10, background: color, transition: "background 0.4s ease" }}
      />
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: "3px solid transparent",
          borderRight: "3px solid transparent",
          borderTop: `5px solid ${color}`,
          transition: "border-color 0.4s ease",
        }}
      />
    </div>
  );
}
